/**
 * One hit test for every card release, shared by the preview and the commit.
 *
 * `document.elementFromPoint` answers this question perfectly, but it has to
 * flush style and layout for the whole page before it can, and a carry dirties
 * both every frame — so asking it per frame bought a full synchronous layout of
 * the battle page 60 times a second. This module asks the same question of
 * cached viewport boxes instead, re-measuring only a few times a second.
 *
 * It exists as one module rather than per-component state because the highlight
 * under the finger (Hand / Cell) and the action that actually fires on release
 * (GameView) MUST agree. When they were two hit tests they could disagree, and
 * every disagreement is a lie: a cell lights up and nothing happens there, or
 * nothing lights up and the card plays anyway. Sharing the resolver — and the
 * measurement behind it — makes that class of bug unrepresentable.
 */

/**
 * Everything whose box can decide where a release lands. Not "everything
 * droppable": callers resolve the deepest element under the point and then walk
 * up to whatever they care about with `closest()`, exactly as they used to walk
 * up from `elementFromPoint`. Staged cards are in here because a plan pile
 * overlaps its own cell's top edge, so the finger can be over a card whose host
 * cell's box does not contain the point.
 *
 * The last two are the lab sandbox's own zones. They cost nothing in a match —
 * neither selector matches anything on the battle page — and they have to be
 * here rather than in a second per-caller list, because a caller asking
 * "is the release anywhere over the board surface at all?" gets a NO from a
 * point this list cannot see, which in the sandbox means unplanning a card the
 * player meant to keep.
 */
const CANDIDATE_SELECTOR = [
  '[data-cell-id]',
  '[data-table-id]',
  '[data-testid="board-stage"]',
  '[data-hand-drop-zone="true"]',
  '[data-testid^="staged-card-"]',
  '[data-passive-drop-side]',
  '.lab-sandbox-board-surface',
].join(', ');

/**
 * How often a gesture re-measures. The zones do not move mid-drag (the battle
 * page does not scroll), but WHICH cells are droppable can change when a
 * snapshot arrives mid-gesture, and a plan pile grows as cards are dropped onto
 * it. Four times a second bounds the staleness to a frame or two of highlight
 * while keeping the forced layout off the per-frame path.
 */
const REMEASURE_MS = 250;

interface Candidate {
  element: HTMLElement;
  rect: DOMRect;
}

interface Measurement {
  measuredAt: number;
  candidates: readonly Candidate[];
}

let measurement: Measurement | undefined;
/**
 * The box a zone had BEFORE it became the drop target. The target lifts and
 * inflates while it is highlighted (`[data-pointer-drag-over]` animates
 * `transform`), and getBoundingClientRect reports transformed boxes — so
 * re-measuring a lifted cell shrinks its own hit region, which made the
 * highlight strobe on and off along the cell's bottom edge.
 */
let pinned: { element: HTMLElement; rect: DOMRect } | undefined;

/**
 * Reverse document order, which is the closest cheap stand-in for paint order:
 * without explicit z-index a later element paints over an earlier one, and a
 * descendant is always later than its ancestor. So the first overlapping hit is
 * both the deepest (a staged card over its own cell) and the topmost (the hand
 * strip over the board it overlaps) — the two cases elementFromPoint used to get
 * right for free.
 */
function measure(now: number): Measurement {
  const candidates = [...document.querySelectorAll<HTMLElement>(CANDIDATE_SELECTOR)]
    .reverse()
    .flatMap((element) => {
      const rect = element === pinned?.element ? pinned.rect : element.getBoundingClientRect();
      // An unlaid-out candidate (jsdom, a just-mounted board) has no box to test
      // against; dropping it here is what sends the caller to the fallback.
      if (rect.width <= 0 || rect.height <= 0) return [];
      return [{ element, rect }];
    });
  const next: Measurement = { measuredAt: now, candidates };
  measurement = next;
  return next;
}

/**
 * The topmost candidate under the point, or — when nothing on the page has a
 * measurable box yet — whatever `elementFromPoint` reports. Callers must treat
 * the result as a starting point and `closest()` up to the zone they mean; both
 * paths return an element that is inside its zone rather than the zone itself.
 */
export function resolveDropCandidate(clientX: number, clientY: number): Element | undefined {
  const now = globalThis.performance?.now() ?? Date.now();
  const current = measurement !== undefined && now - measurement.measuredAt <= REMEASURE_MS
    ? measurement
    : measure(now);
  if (current.candidates.length === 0) {
    return typeof document.elementFromPoint === 'function'
      ? document.elementFromPoint(clientX, clientY) ?? undefined
      : undefined;
  }
  return current.candidates.find(({ rect }) => clientX >= rect.left
    && clientX <= rect.right
    && clientY >= rect.top
    && clientY <= rect.bottom)?.element;
}

/**
 * Freezes `element`'s current box for as long as it stays the drop target, so
 * the lift animation the highlight triggers cannot move the region that decides
 * the highlight. Pass `undefined` when the target clears.
 */
export function pinDropZoneRect(element: HTMLElement | undefined): void {
  if (element === undefined) {
    pinned = undefined;
    return;
  }
  const known = measurement?.candidates.find((candidate) => candidate.element === element);
  pinned = { element, rect: known?.rect ?? element.getBoundingClientRect() };
}

/** Drops the measurement at a gesture boundary, so the next one starts fresh. */
export function invalidateDropZones(): void {
  measurement = undefined;
  pinned = undefined;
}
